import React, { FC } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { AGGREGATOR_TYPE } from "../../../data/outlet-status-constants";
import { getAggregatorLogo } from "../../../helpers/accounting-helper";
import {
  ONLINE_ORDER_RECON_INTRO_STEPS,
  UPLOAD_STATUS_TYPES,
} from "../../../data/accounting-constants";
import { CTA_SIZE, CTA_TYPE, DATE_TIME_FORMATS } from "../../../data/constants";
import { convertMomentToString } from "../../../helpers/moment-helper";
import CtaComponent from "../../../components/reusable-ui/cta";
import RenderIntroSteps from "../../../components/reusable-ui/intro-steps";

interface Props {
  handleViewReport: any;
}

const UploadedReportsView: FC<Props> = (props: Props) => {
  const { handleViewReport } = props;
  const {
    businessBranchesDataMap,
    allChannelsDefDataMap,
    uploadedReportsMetaData,
  } = useSelector((state: RootState) => ({
    businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
    allChannelsDefDataMap: state.businessReducer.allChannelsDefDataMap,
    uploadedReportsMetaData: state.accountingReducer.uploadedReportsMetaData,
  }));

  function renderIntroScreen() {
    return (
      <div
        className={`flex flex-col items-center justify-center m-20 bg-white
          h-[calc(100vh-206px)] w-[calc(100%-40px)]
        `}
      >
        <p className="text-14 text-[#666666] leading-none mb-14">
          New to this feature?
        </p>
        <p className="text-24 text-[#2A77F2] leading-none font-bold mb-30">
          Lets get you started !
        </p>
        <RenderIntroSteps introStepsList={ONLINE_ORDER_RECON_INTRO_STEPS} />
      </div>
    );
  }

  function renderBranchChannelName(metaData: any) {
    return (
      <div className="text-14 font-semibold w-1/5">
        {businessBranchesDataMap[metaData?.branchId]?.branchName}
        {metaData?.branchId && metaData?.channelDefId ? " - " : ""}
        {allChannelsDefDataMap[metaData?.channelDefId]?.name}
      </div>
    );
  }

  function renderAggregatorInfo(metaData: any) {
    return (
      <div className="flex items-center w-1/5">
        <img
          src={getAggregatorLogo(metaData?.aggregatorType)}
          alt=""
          width={24}
          height={24}
        />
        <p className="text-14 text-[#666666] ml-10">
          {AGGREGATOR_TYPE[metaData?.aggregatorType]?.label}
        </p>
      </div>
    );
  }

  function renderUploadDate(metaData: any) {
    return (
      <div className="text-14 text-[#666666] w-1/5 text-center">
        Uploaded on{" "}
        {convertMomentToString(
          metaData?.createdDate,
          DATE_TIME_FORMATS.dayMonthYear
        )}
      </div>
    );
  }

  function renderUploadStatus(metaData: any) {
    return (
      <p
        className={`text-14 whitespace-nowrap ${
          metaData?.uploadStatus === UPLOAD_STATUS_TYPES.failed.type
            ? "text-[#AB3737] font-semibold"
            : ""
        }`}
      >
        {UPLOAD_STATUS_TYPES[metaData?.uploadStatus]?.label}
      </p>
    );
  }

  function renderSingleReportInfo(metaData: any) {
    let startDate = convertMomentToString(
        metaData?.startDate,
        DATE_TIME_FORMATS.dayMonthYear
      ),
      endDate = convertMomentToString(
        metaData?.endDate,
        DATE_TIME_FORMATS.dayMonthYear
      ),
      payload = {
        branchId: metaData?.branchId,
        channelDefId: metaData?.channelDefId,
        aggregatorType: metaData?.aggregatorType,
        dateRange: {
          startDate,
          endDate,
        },
      };
    return (
      <div
        className="flex items-center py-14 border-b border-[#DEDEDF]"
        key={metaData?.mid}
      >
        {renderBranchChannelName(metaData)}
        {renderAggregatorInfo(metaData)}
        <div className="text-14 text-[#666666] w-1/5">
          {`${startDate} - ${endDate}`}
        </div>
        {renderUploadDate(metaData)}
        <div className="w-1/5 flex items-center justify-center">
          {metaData?.uploadStatus === UPLOAD_STATUS_TYPES.processed.type ? (
            <CtaComponent
              ctaText="View Report"
              ctaSize={CTA_SIZE.small}
              ctaType={CTA_TYPE.primary_cta}
              onCtaClick={() => handleViewReport(payload)}
              customClass={"text-[#2A77F2]"}
            />
          ) : (
            renderUploadStatus(metaData)
          )}
        </div>
      </div>
    );
  }

  function renderUploadedReportsList() {
    return (
      <div className="mx-20 mt-20 p-28 pl-32 bg-white min-h-[50vh]">
        <p className="text-19 font-bold mb-16">Last Uploaded Reports</p>
        {uploadedReportsMetaData?.length
          ? uploadedReportsMetaData?.map((metaData: any) =>
              renderSingleReportInfo(metaData)
            )
          : null}
      </div>
    );
  }

  return uploadedReportsMetaData?.length
    ? renderUploadedReportsList()
    : renderIntroScreen();
};

export default UploadedReportsView;
